import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  GridLegacy as Grid,
  LinearProgress,
  Modal,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import {
  BulkRecreateGroup,
  useBulkRecreateSurchargeDateBlocks,
} from '~/hooks/apis/reservations/useBulkRecreateSurchargeDateBlocks';
import { useGetExistingSurchargesByRoomRate } from '~/hooks/apis/reservations/useGetExistingSurchargesByRoomRate';
import { useGetSalesforceSurcharges } from '~/hooks/apis/reservations/useGetSalesforceSurcharges';

import ReservationService from '~/services/ReservationService';
import { formatDateISO } from '~/services/TimeService';
import { getFxRates } from '~/services/fxService';

import type { ResolvedSurchargeBlock, SalesforceSurchargePeriod } from '~/types/surcharges';

import { convertValueByFX } from '~/utils/convertValueByFX';
import { daysOfWeek } from '~/utils/daysOfWeek';
import presentFXRates from '~/utils/presentFXRates';
import { resolveSurchargeOverlaps } from '~/utils/resolveSurchargeOverlaps';
import { getMixedAccumulateConflictPairs, hasMixedAccumulateOverlap } from '~/utils/surchargeOverlapAnalysis';

import { SalesforceSurchargesSummary } from './SalesforceSurchargesSummary';
import { NonAccumulatingConflictDialog } from './components/NonAccumulatingConflictDialog';
import { OfferListSelection } from './components/OfferListSelection';
import { RoomRateSelection } from './components/RoomRateSelection';
import { SurchargesSelection } from './components/SurchargesSelection';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(1200px, 96vw)',
  height: 'min(920px, 96vh)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
};

const stepsList = ['Select Opportunity', 'Select Salesforce surcharges', 'Select Room Rates', 'Summary'];

const SELECT_OPPORTUNITY_STEP = 0;
const SELECT_SALESFORCE_SURCHARGES_STEP = 1;
const SELECT_ROOM_RATES_STEP = 2;
const SUMMARY_STEP = 3;

interface Props {
  roomTypes: Array<App.RoomType>;
  offers: Array<App.Offer>;
  propertyId: string;
  closeModal: () => void;
  initialOfferId?: string;
  isOpen: boolean;
  vendorId: string;
}

export function SalesforceSurchargesModal({
  closeModal,
  initialOfferId,
  isOpen,
  roomTypes,
  offers,
  propertyId,
  vendorId,
}: Props) {
  const initialStep = initialOfferId ? SELECT_SALESFORCE_SURCHARGES_STEP : SELECT_OPPORTUNITY_STEP;
  const [step, setStep] = useState<number>(initialStep);
  const [stepError, setStepError] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState(null);
  const [success, setSuccess] = useState(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const wasOpenRef = useRef(false);

  useEffect(() => () => clearTimeout(dismissTimerRef.current), []);

  const [selectedOffer, setSelectedOffer] = useState<null | string>(initialOfferId ?? null);

  const [selectedSurcharges, setSelectedSurcharges] = useState<Array<SalesforceSurchargePeriod>>([]);
  const [nonAccuDialogOpen, setNonAccuDialogOpen] = useState(false);
  const [nonAccuPairIndex, setNonAccuPairIndex] = useState(0);
  const [hasConfirmedNonAccuConflict, setHasConfirmedNonAccuConflict] = useState(false);

  const resolvedBlocks: Array<ResolvedSurchargeBlock> = useMemo(
    () => resolveSurchargeOverlaps(selectedSurcharges),
    [selectedSurcharges],
  );

  const mixedConflictPairs = useMemo(() => getMixedAccumulateConflictPairs(selectedSurcharges), [selectedSurcharges]);

  const isStepFailed = (step: number) => {
    return step === stepError;
  };

  const filteredRoomRates = useMemo(() => {
    return roomTypes
      .map((roomType) => {
        const filteredRoomRates = roomType.room_rates.filter((roomRate) => {
          return roomRate.rate_plan.product_type === 'limited_time_exclusive';
        });
        return {
          ...roomType,
          room_rates: filteredRoomRates,
        };
      })
      .reduce((accumulator, roomType) => {
        roomType.room_rates.forEach((roomRate) => {
          accumulator.push({
            propertyId,
            uniqueId: `${roomType.id}-${roomRate.rate_plan.id}`,
            id: `${roomType.id}-${roomRate.rate_plan.id}`,
            roomType: roomType,
            roomRate: roomRate,
          });
        });

        return accumulator;
      }, []);
  }, [roomTypes, propertyId]);

  const {
    data: salesforceSurcharges = [],
    isLoading: isSurchargesLoading,
    error: surchargesError,
  } = useGetSalesforceSurcharges(selectedOffer);

  const [selectedRoomRates, setSelectedRoomRates] = useState([]);
  const [fetchExistingEnabled, setFetchExistingEnabled] = useState(false);

  const {
    data: existingSurchargesByRoomRate = {},
    isFetching: isExistingSurchargesFetching,
    refetch: refetchExistingSurcharges,
  } = useGetExistingSurchargesByRoomRate(
    propertyId,
    selectedRoomRates,
    fetchExistingEnabled && selectedRoomRates.length > 0,
  );

  const [premiseMismatch, setPremiseMismatch] = useState(false);

  const bulkRecreateSurchargeDateBlocks = useBulkRecreateSurchargeDateBlocks();

  useEffect(() => {
    if (surchargesError) {
      setErrors(surchargesError);
    }
  }, [surchargesError]);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) {
      return;
    }
    wasOpenRef.current = true;

    setErrors(null);
    setFetchExistingEnabled(false);
    setHasConfirmedNonAccuConflict(false);
    setNonAccuPairIndex(0);
    setPremiseMismatch(false);
    setSelectedOffer(initialOfferId ?? null);
    setSelectedRoomRates([]);
    setSelectedSurcharges([]);
    setStep(initialOfferId ? SELECT_SALESFORCE_SURCHARGES_STEP : SELECT_OPPORTUNITY_STEP);
    setStepError(null);
    setSuccess(null);
  }, [initialOfferId, isOpen]);

  useEffect(() => {
    setHasConfirmedNonAccuConflict(false);
    setNonAccuPairIndex(0);
    setFetchExistingEnabled(false);
  }, [selectedSurcharges]);

  useEffect(() => {
    setFetchExistingEnabled(false);
  }, [selectedRoomRates]);

  const onSelectSurcharges = useCallback(
    (selected: Array<string | number>) => {
      setErrors(null);
      setSelectedSurcharges(salesforceSurcharges.filter((s) => selected.includes(s.id)));
    },
    [salesforceSurcharges],
  );

  const shouldHideHeader = step === SELECT_SALESFORCE_SURCHARGES_STEP;

  const onSelectRoomRates = useCallback(
    (selected) => {
      setSelectedRoomRates(filteredRoomRates.filter((r) => selected.includes(r.id)));
    },
    [filteredRoomRates, setSelectedRoomRates],
  );

  const handleStep = (nextStep: number) => {
    if (nextStep > step) {
      if (nextStep >= SELECT_SALESFORCE_SURCHARGES_STEP && !selectedOffer) {
        setErrors('Please select an offer');
        setStepError(0);
        return;
      }
      if (nextStep >= SELECT_ROOM_RATES_STEP && !selectedSurcharges.length) {
        setErrors('Please select surcharges');
        setStepError(1);
        return;
      }
      if (nextStep >= SUMMARY_STEP && !selectedRoomRates.length) {
        setErrors('Please select room rates');
        setStepError(2);
        return;
      }
      if (nextStep === SUMMARY_STEP && hasMixedAccumulateOverlap(selectedSurcharges) && !hasConfirmedNonAccuConflict) {
        setNonAccuPairIndex(0);
        setNonAccuDialogOpen(true);
        return;
      }
      if (nextStep === SUMMARY_STEP) {
        setFetchExistingEnabled(true);
      }
    }
    if (stepError !== SUMMARY_STEP) {
      setStepError(null);
    }
    setErrors(null);
    setStep(nextStep);
    return;
  };

  const handleConfirmNonAccuDialog = () => {
    if (nonAccuPairIndex < mixedConflictPairs.length - 1) {
      setNonAccuPairIndex(nonAccuPairIndex + 1);
      return;
    }
    setHasConfirmedNonAccuConflict(true);
    setNonAccuDialogOpen(false);
    if (stepError !== SUMMARY_STEP) {
      setStepError(null);
    }
    setErrors(null);
    setStep(SUMMARY_STEP);
    setFetchExistingEnabled(true);
  };

  const getCurrencies = async (propertyId, roomTypeId, roomRateId) => {
    const surchargeSchema = await ReservationService.getSurchargeDateBlockSchema(propertyId, roomTypeId, roomRateId);
    const currencyCodes = Object.keys(surchargeSchema.post.body.schema.properties.prices.properties);
    return {
      [`${roomTypeId}-${roomRateId}`]: currencyCodes,
    };
  };

  const mapPayload = useCallback(async () => {
    const blockMapper = (block: ResolvedSurchargeBlock, roomRateId: string, currencyCodes: Array<string>, fxRates) => {
      const item: Record<string, unknown> = {
        room_rate_id: roomRateId,
        start_date: formatDateISO(block.start_date),
        end_date: formatDateISO(block.end_date),
        cost_amount: block.cost_price,
        cost_currency: block.currency_code,
        prices: {
          [block.currency_code]: block.sell_price,
        },
        monday: !!block.monday,
        tuesday: !!block.tuesday,
        wednesday: !!block.wednesday,
        thursday: !!block.thursday,
        friday: !!block.friday,
        saturday: !!block.saturday,
        sunday: !!block.sunday,
      };

      const prices = item.prices as Record<string, number>;
      currencyCodes.forEach((currencyCode) => {
        const converted = convertValueByFX({
          fxRates,
          fromCurrency: 'AUD',
          toCurrency: currencyCode,
          value: block.sell_price,
        });
        prices[currencyCode] = Math.round(converted);
      });

      return item;
    };

    const rangesIntersect = (aStart: string, aEnd: string, bStart: string, bEnd: string) =>
      aStart <= bEnd && aEnd >= bStart;

    setLoading(true);
    setErrors(null);
    setStepError(null);
    setSuccess(null);
    setPremiseMismatch(false);
    try {
      const blocks = resolvedBlocks;
      const fxRatesResponse = await getFxRates();
      const fxRates = presentFXRates(fxRatesResponse);

      const currenciesPayload = await Promise.all(
        selectedRoomRates.map((rr) => getCurrencies(propertyId, rr.roomType.id, rr.roomRate.id)),
      ).then((result) =>
        result.reduce((acc, current) => {
          return Object.assign(acc, current);
        }, {}),
      );

      // Group toCreate / toDelete by roomTypeId — bulkRecreate is a per-roomType call.
      const groupsByRoomType = new Map<string, BulkRecreateGroup>();

      for (const roomRate of selectedRoomRates) {
        const roomTypeId = roomRate.roomType.id;
        const roomRateId = roomRate.roomRate.id;
        const currencyKey = `${roomTypeId}-${roomRateId}`;
        const currencyCodes = currenciesPayload[currencyKey];

        const incomingForRate = blocks.map((block) => blockMapper(block, roomRateId, currencyCodes, fxRates));

        // Premise: existing vendor blocks for this room_rate that overlap an incoming
        // block on BOTH date range and at least one shared day-of-week. Date-only
        // overlap would clobber disjoint-DoW siblings (e.g. existing weekend +
        // incoming weekday over the same dates) — the very bug this migration fixes.
        const existingForRate = (existingSurchargesByRoomRate[roomRateId] ?? []).filter(
          (b) => (b.surcharge_type ?? 'vendor') === 'vendor',
        );
        const intersectingIds = existingForRate
          .filter((existing) =>
            blocks.some(
              (incoming) =>
                rangesIntersect(
                  existing.start_date,
                  existing.end_date,
                  formatDateISO(incoming.start_date),
                  formatDateISO(incoming.end_date),
                ) && daysOfWeek.some((d) => existing[d.key] && incoming[d.key]),
            ),
          )
          .map((b) => b.id);

        const group = groupsByRoomType.get(roomTypeId) ?? {
          propertyId,
          roomTypeId,
          surchargeType: 'vendor',
          toDelete: [],
          toCreate: [],
        };
        group.toCreate.push(...incomingForRate);
        group.toDelete.push(...intersectingIds);
        groupsByRoomType.set(roomTypeId, group);
      }

      // svc-reservation rejects vendor blocks with non-positive cost_amount or
      // any non-positive entry in prices (FX rounding can land a 0 for small
      // AUD values converted into a strong currency). Surface the offending
      // rows here so the user sees them in the modal, not as a 400 from the
      // backend.
      const priceIssues: Array<string> = [];
      for (const group of groupsByRoomType.values()) {
        for (const block of group.toCreate) {
          const rateId = block.room_rate_id as string;
          const range = `${block.start_date as string}–${block.end_date as string}`;
          const costAmount = block.cost_amount as number;
          if (!(costAmount > 0)) {
            priceIssues.push(`Room rate ${rateId} ${range}: cost amount must be > 0 (got ${costAmount})`);
          }
          const prices = block.prices as Record<string, number>;
          for (const [currency, price] of Object.entries(prices)) {
            if (!(price > 0)) {
              priceIssues.push(`Room rate ${rateId} ${range}: ${currency} sell price must be > 0 (got ${price})`);
            }
          }
        }
      }
      if (priceIssues.length > 0) {
        throw new Error(
          `Surcharge prices must be positive. Fix the source surcharges and retry:\n${priceIssues.join('\n')}`,
        );
      }

      await bulkRecreateSurchargeDateBlocks.mutateAsync(Array.from(groupsByRoomType.values()));

      setLoading(false);
      setSuccess('Surcharges successfully added');
      dismissTimerRef.current = setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (e) {
      setLoading(false);
      if (e?.status === 409 && e?.detail) {
        setPremiseMismatch(true);
      } else {
        setErrors(e.message);
      }
      setStepError(3);
    }
  }, [
    selectedRoomRates,
    resolvedBlocks,
    propertyId,
    closeModal,
    bulkRecreateSurchargeDateBlocks,
    existingSurchargesByRoomRate,
  ]);

  const handleRefreshExisting = useCallback(async () => {
    setPremiseMismatch(false);
    setStepError(null);
    setErrors(null);
    await refetchExistingSurcharges();
  }, [refetchExistingSurcharges]);

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <NonAccumulatingConflictDialog
          open={nonAccuDialogOpen}
          onCancel={() => setNonAccuDialogOpen(false)}
          onConfirm={handleConfirmNonAccuDialog}
          pair={mixedConflictPairs[nonAccuPairIndex] ?? null}
          currentIndex={nonAccuPairIndex}
          totalPairs={mixedConflictPairs.length}
        />
        {!shouldHideHeader && (
          <Box
            sx={{
              minHeight: 44,
              p: 2,
              backgroundColor: 'secondary.dark',
              color: 'white',
            }}
          >
            {step === SELECT_OPPORTUNITY_STEP && (
              <Typography variant="h5" component="h5" m={0}>
                Select an offer
              </Typography>
            )}
            {step === SELECT_ROOM_RATES_STEP && (
              <Typography variant="h5" component="h5" m={0}>
                Select room rates to apply surcharges to
              </Typography>
            )}
            {step === SUMMARY_STEP && (
              <Typography variant="h5" component="h5" m={0}>
                Review and confirm
              </Typography>
            )}
          </Box>
        )}
        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            position: 'relative',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {(loading || isSurchargesLoading) && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
              }}
            />
          )}
          <Grid container direction="column" justifyContent="space-between" sx={{ height: '100%' }}>
            <Grid item sx={{ mb: 2, mt: shouldHideHeader ? 0 : 1 }}>
              <Stepper nonLinear activeStep={step}>
                {stepsList.map((label, index) => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: {
                    optional?: ReactNode;
                    error?: boolean;
                  } = {};
                  if (isStepFailed(index)) {
                    labelProps.optional = (
                      <Typography variant="caption" color="error">
                        Alert message
                      </Typography>
                    );
                    labelProps.error = true;
                  }
                  return (
                    <Step key={label} {...stepProps}>
                      <StepButton color="inherit" onClick={() => handleStep(index)}>
                        <StepLabel {...labelProps}>{label}</StepLabel>
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
            </Grid>
            <Grid item xs sx={{ minHeight: 0 }}>
              {step === SELECT_OPPORTUNITY_STEP && (
                <OfferListSelection offers={offers} onSelectOffer={setSelectedOffer} selectedOffer={selectedOffer} />
              )}
              {step === SELECT_SALESFORCE_SURCHARGES_STEP && (
                <SurchargesSelection
                  salesforceSurcharges={salesforceSurcharges}
                  selectedSurcharges={selectedSurcharges}
                  onSelectSurcharges={onSelectSurcharges}
                />
              )}
              {step === SELECT_ROOM_RATES_STEP && (
                <RoomRateSelection
                  roomRates={filteredRoomRates}
                  propertyId={propertyId}
                  selectedRoomRates={selectedRoomRates}
                  onSelectRoomRates={onSelectRoomRates}
                  vendorId={vendorId}
                />
              )}
              {step === SUMMARY_STEP && (
                <SalesforceSurchargesSummary
                  selectedSurcharges={selectedSurcharges}
                  resolvedBlocks={resolvedBlocks}
                  selectedRoomRates={selectedRoomRates}
                  existingSurchargesByRoomRate={existingSurchargesByRoomRate}
                  propertyId={propertyId}
                  vendorId={vendorId}
                  setStep={setStep}
                />
              )}
            </Grid>
          </Grid>
        </Box>
        {(errors || success || premiseMismatch) && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Stack sx={{ width: '100%' }} spacing={1}>
              {premiseMismatch && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRefreshExisting}
                      disabled={isExistingSurchargesFetching}
                    >
                      {isExistingSurchargesFetching ? 'Refreshing…' : 'Refresh & retry'}
                    </Button>
                  }
                >
                  The surcharge state on the server has changed since this view was loaded. Refresh to re-fetch existing
                  surcharges, then click Save again.
                </Alert>
              )}
              {errors && <Alert severity="error">{errors}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}
            </Stack>
          </Box>
        )}
        <Box sx={{ p: 2 }}>
          <Grid container direction="row" justifyContent="end" alignItems="center" gap={2}>
            {step > SELECT_OPPORTUNITY_STEP && (
              <Button
                color="secondary"
                variant="text"
                onClick={() => setStep(step - 1)}
                disabled={loading || !!success}
              >
                Back
              </Button>
            )}
            {step < SUMMARY_STEP && (
              <Button variant="contained" onClick={() => handleStep(step + 1)}>
                Continue
              </Button>
            )}
            {step === SUMMARY_STEP && !success && (
              <Button
                variant="contained"
                color="success"
                onClick={mapPayload}
                disabled={loading || isExistingSurchargesFetching}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {loading ? 'Saving…' : 'Save'}
              </Button>
            )}
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
}
